import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, unref as _unref, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-center text-gray-600 py-4"
}
const _hoisted_2 = {
  id: "payment-element",
  class: "transition-opacity duration-300 mt-6"
}
const _hoisted_3 = { class: "mt-4" }
const _hoisted_4 = { class: "w-full text-left text-sm/6 whitespace-nowrap" }
const _hoisted_5 = { class: "max-w-0 px-0 py-5 align-top" }
const _hoisted_6 = { class: "truncate font-medium text-gray-900" }
const _hoisted_7 = { class: "py-5 pr-0 pl-8 text-right align-top text-gray-700 tabular-nums" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { key: 2 }
const _hoisted_11 = { class: "pb-4 pt-4 pr-0 pl-8 text-right font-semibold text-black tabular-nums" }
const _hoisted_12 = { class: "border-t border-zinc-100" }
const _hoisted_13 = { class: "pt-4 pr-0 pb-0 pl-8 text-right text-lg font-semibold text-gray-900 tabular-nums" }
const _hoisted_14 = { class: "my-6 flex justify-end" }
const _hoisted_15 = { class: "flex flex-col-reverse items-center justify-end gap-3 *:w-full sm:flex-row sm:*:w-auto" }
const _hoisted_16 = { class: "mt-6 space-y-4" }
const _hoisted_17 = { class: "mt-8 flex flex-col-reverse items-center justify-between gap-3 *:w-full sm:flex-row sm:*:w-auto" }
const _hoisted_18 = { class: "space-y-4" }
const _hoisted_19 = { class: "!font-bold" }

import {
		nextTick,
		ref,
		computed,
	} from 'vue'

	import {
		loadStripe,
	} from '@stripe/stripe-js/pure'

	import {
		Invoice
	} from '@/types'
	
	import {
		store as _userStore
	} from '@/store/user'

	import api from '@/utilities/api'

	import {
		useBrand
	} from '@/utilities/brand'

	
export default /*@__PURE__*/_defineComponent({
  __name: 'PayButton',
  props: {
		invoice: {
			type: Object,
			default: () => ({}) as Invoice,
		},
	},
  emits: [
		"submitted",
		"verification-canceled",
	],
  setup(__props, { emit: __emit }) {

	const brand = useBrand()

	const props = __props

	const currencyFormatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	})


	const payInvoiceModal = ref<{ open: boolean }>({ open: false })
	const stripe = ref<any>(null)
	const elements = ref<any>(null)
	const clientSecret = ref('')
	const financialConnectionsClientSecret = ref('')
	const customerSessionClientSecret = ref('')
	const isSubmitting = ref(false)
	const stripeElementsLoaded = ref(false)
	const store = _userStore()
	const contactId = ref(store.user.cognitoUser.attributes["custom:fm_contact_id"])
	const stripePromise = loadStripe(process.env.VUE_APP_STRIPE_API_KEY || "")
	const contactEmail = ref(store.user.cognitoUser.attributes.email)
	const needsMicrodepositVerification = ref(false)
	const verificationHostedUrl = ref("")
	const isSwitchingPaymentMethod = ref(false)
	const verificationWasCanceled = ref(false)

	const emit = __emit

	// the parent clears paymentStatus on the row when a verification is canceled, so reading the
	// prop stays accurate without a local override
	const isPendingVerification = computed(() => {
		return props.invoice?.paymentStatus === "pending_verification"
	})

	const currentPaymentIntentId = computed(() => clientSecret.value.split("_secret")[0])

	// determines whether a retrieved PaymentIntent is parked on a microdeposit verification.
	// next_action is checked rather than status, since Stripe clears next_action once the intent is
	// canceled or confirmed and the status name for this state varies by API version
	function isAwaitingMicrodepositVerification(paymentIntent: any) {
		return paymentIntent?.next_action?.type === "verify_with_microdeposits"
	}

	async function openDialog() {
		payInvoiceModal.value.open = true
		await createPaymentIntent()
	}

	async function createPaymentIntent() {
		try {
			stripeElementsLoaded.value = false
			needsMicrodepositVerification.value = false

			const body = {
				amount: props?.invoice?.amountDue,
				currency: 'usd',
				filemakerId: contactId.value,
				invoiceId: props?.invoice?.id,
				invoiceNumber: props?.invoice?.invoiceNumber,
			}

			const response = await api("post", "fmDataAPI", "/payment-intent", body)

			if (!response.clientSecret) {
				throw new Error('Invalid PaymentIntent response')
			}

			clientSecret.value = response.clientSecret
			financialConnectionsClientSecret.value = response.financialConnectionsClientSecret
			customerSessionClientSecret.value = response.customerSessionClientSecret
			await mountStripeElements()
			stripeElementsLoaded.value = true
		}
		catch (error) {
			console.error('Error creating PaymentIntent:', error)
			alert('Unable to initiate payment. Please try again.')
		}
	}

	const selectedPaymentMethod = ref("")

	async function mountStripeElements() {
		const elementsOptions = {
			clientSecret: clientSecret.value,
			financialConnectionsClientSecret: financialConnectionsClientSecret.value,
			customerSessionClientSecret: customerSessionClientSecret.value,
			appearance: {
				theme: 'flat',
				variables: {
					fontWeightNormal: '500',
					colorPrimary: brand.stripeColorPrimary,
					borderRadius: '6px',
					tabSpacing: `8px`,
					gridColumnSpacing: `16px`,
					gridRowSpacing: `24px`,
				},
				rules: {
					'.Input': {
						backgroundColor: '#fff',
						border: '1px solid rgba(9,9,11,.1)'
					},
					'.CheckboxInput': {
						backgroundColor: '#fff',
						border: '1px solid rgba(9,9,11,.1)'
					},
				}
			},
		}
		const options = {
			layout: {
				type: 'tabs',
				defaultCollapsed: false,
			},
			fields: {
				billingDetails: {
					email: "never",
				}
			}
		}
		stripe.value = await stripePromise

		// check for existing payment intent
		// show the verification panel instead of the payment form if microdeposits are still required
		const { paymentIntent } = await stripe.value.retrievePaymentIntent(clientSecret.value)

		if (isAwaitingMicrodepositVerification(paymentIntent)) {
			verificationHostedUrl.value = paymentIntent.next_action.verify_with_microdeposits?.hosted_verification_url || ""
			needsMicrodepositVerification.value = true
			stripeElementsLoaded.value = true

			return
		}

		// else create new intent
		const elementsInstance = stripe.value.elements(elementsOptions)
		const paymentElement = elementsInstance.create('payment', options)
		paymentElement.mount('#payment-element')
		elements.value = elementsInstance

		paymentElement.on('change', async function(event: any) {
			await nextTick()
			selectedPaymentMethod.value = event.value.payment_method?.type || event.value.type
		})
	}

	async function submitPayment() {
		if (!stripe.value || !elements.value) return
		isSubmitting.value = true

		// update invoice in FIleMaker to ensure processing fee line items is propely applied or removed based on selected payment method
		let updatedInvoice: Invoice
		try {
			updatedInvoice = await api("post", "fmDataAPI", `/fmDataAPI`, {
				action: "updateInvoice",
				payload: {
					ccFee: selectedPaymentMethod.value === 'card' ? props.invoice.ccFee : 0,
					id: props.invoice.id,
				},
			})

			// alert user if updated invoice total + fees is different than what they are about to pay
			if (Math.round(amountDueAfterProcessingFees.value * 100) / 100 !== updatedInvoice.amountDue) {
				alert('Unable to validate invoice amount. Please try again.')
				isSubmitting.value = false
				return
			}
		}
		catch(error: any) {
			console.error(error)
			alert('Unable to validate invoice amount. Please try again.')
			isSubmitting.value = false
			return
		}

		// update payment intent to ensure amount is correct in case user made changes to invoice or there are processing fees that need to be added
		try {
			await api("patch", "fmDataAPI", `/payment-intent/${currentPaymentIntentId.value}`, {
				amount: updatedInvoice.amountDue,
			})
		}
		catch(error: any) {
			console.error("Error updating payment intent:", error)
			alert('Unable to update payment details. Please try again.')
			isSubmitting.value = false
			return
		}

		const { error, paymentIntent } = await stripe.value.confirmPayment({
			elements: elements.value,
			redirect: 'if_required',
			confirmParams: {
				payment_method_data: {
					billing_details: {
						email: contactEmail.value,
					},
				},
			},
		})

		if (error) {
			console.error(error.message)
			alert(error.message)
		}
		else if (isAwaitingMicrodepositVerification(paymentIntent)) {
			// keep the modal open on the verification panel so the customer can still back out
			verificationHostedUrl.value = paymentIntent.next_action.verify_with_microdeposits?.hosted_verification_url || ""
			needsMicrodepositVerification.value = true
			verificationWasCanceled.value = false

			openVerificationWindow()
		}
		else if (paymentIntent?.status === 'processing') {
			payInvoiceModal.value.open = false
			setIsOpen(true)
		}
		else if (paymentIntent?.status === 'succeeded') {
			payInvoiceModal.value.open = false
			setIsOpen(true)
		}

		isSubmitting.value = false
	}

	const showProcessingFee = computed(() => {
		return selectedPaymentMethod.value === 'card' && (props.invoice.ccFee > 0) && (props.invoice.amountDue > 0)
	})

	// cancels the PaymentIntent currently held in clientSecret
	async function cancelCurrentPaymentIntent() {
		const body = {
			paymentIntentId: currentPaymentIntentId.value
		}

		await api("delete", "fmDataAPI", "/payment-intent", body)
	}

	// cancels the intent behind a closed modal. a pending verification is left alone so the customer
	// can still finish it later, and the live status is checked rather than
	// needsMicrodepositVerification, which is still false while the form is loading
	async function cancelUnconfirmedPayment() {
		try {
			const { paymentIntent } = await stripe.value.retrievePaymentIntent(clientSecret.value)

			if (!isAwaitingMicrodepositVerification(paymentIntent)) {
				await cancelCurrentPaymentIntent()
			}
		}
		catch(error: any) {
			console.error(error)
		}

		// refetch on close so the cleared verification badge is reflected in the invoice list
		if (verificationWasCanceled.value) {
			emit("submitted")
		}
	}

	// opens the Stripe hosted page where the customer enters the microdeposit amount. clicking through
	// commits them to ACH, so the modal closes rather than offering the alternate method behind it
	function openVerificationWindow() {
		if (!verificationHostedUrl.value) return

		openCenteredWindow(verificationHostedUrl.value, "_blank", 500, 600)
		payInvoiceModal.value.open = false
	}

	// abandons the pending microdeposit verification and reloads the payment form so the
	// customer can choose a different method
	async function switchPaymentMethod() {
		isSwitchingPaymentMethod.value = true

		try {
			await cancelCurrentPaymentIntent()
		}
		catch(error: any) {
			console.error("Error canceling pending verification:", error)
			alert("Unable to cancel the pending verification. Please try again.")
			isSwitchingPaymentMethod.value = false
			return
		}

		verificationWasCanceled.value = true
		verificationHostedUrl.value = ""

		// FileMaker clears its own status from the payment_intent.canceled webhook, so tell the
		// parent to clear the row now rather than leaving a stale badge until the next fetch
		emit("verification-canceled")
		await createPaymentIntent()

		isSwitchingPaymentMethod.value = false
	}

	const servicesWithoutProcessingFees = computed(() => {
		return props.invoice.services.filter((s: {description: string, total: number}) => {
			return !s.description.toLowerCase().includes('processing fee')
		})
	})

	const totalServicesWithoutProcessingFees = computed(() => {
		return servicesWithoutProcessingFees.value.reduce((total: number, service: {total: number}) => {
			return total + service.total
		}, 0)
	})

	const amountDueAfterProcessingFees = computed(() => {
		return +totalServicesWithoutProcessingFees.value + ( selectedPaymentMethod.value === 'card' ? props.invoice.ccFee : 0 ) + +(props.invoice?.tax || 0) - +props.invoice.amountPaid
	})

	// opens a popup window centered on the user's screen
	function openCenteredWindow(url: string, name: string, width: number, height: number) {
		const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2)
		const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2)

		window.open(url, name, `width=${width},height=${height},left=${left},top=${top}`)
	}

	const alertIsOpen = ref(false)
	function setIsOpen(value: boolean = false) {
		alertIsOpen.value = value
	}

	function closeAlert() {
		alertIsOpen.value = false
		emit("submitted")
	}

return (_ctx: any,_cache: any) => {
  const _component_tw_button = _resolveComponent("tw-button")!
  const _component_tw_loading = _resolveComponent("tw-loading")!
  const _component_v_heading = _resolveComponent("v-heading")!
  const _component_tw_modal = _resolveComponent("tw-modal")!
  const _component_v_alert_title = _resolveComponent("v-alert-title")!
  const _component_v_strong = _resolveComponent("v-strong")!
  const _component_v_text = _resolveComponent("v-text")!
  const _component_v_alert_body = _resolveComponent("v-alert-body")!
  const _component_v_alert_actions = _resolveComponent("v-alert-actions")!
  const _component_v_alert = _resolveComponent("v-alert")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_component_tw_button, {
      class: "btn-base btn-indigo w-48",
      size: "small",
      variant: "primary",
      onClick: openDialog
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(isPendingVerification.value ? "Verify Deposit" : "Pay Now"), 1)
      ]),
      _: 1
    }),
    _createVNode(_component_tw_modal, {
      title: `Pay Invoice #${props?.invoice?.invoiceNumber}`,
      "modal-max-width": "max-w-screen-md",
      ref_key: "payInvoiceModal",
      ref: payInvoiceModal,
      "close-modal": cancelUnconfirmedPayment
    }, {
      default: _withCtx(() => [
        _withDirectives(_createElementVNode("div", null, [
          (!stripeElementsLoaded.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _cache[1] || (_cache[1] = _createTextVNode(" Loading payment form... ")),
                _createVNode(_component_tw_loading)
              ]))
            : _createCommentVNode("", true),
          _withDirectives(_createElementVNode("div", _hoisted_2, null, 512), [
            [_vShow, stripeElementsLoaded.value]
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_3, [
            _createVNode(_component_v_heading, null, {
              default: _withCtx(() => _cache[2] || (_cache[2] = [
                _createTextVNode(" Payment Details ")
              ])),
              _: 1,
              __: [2]
            }),
            _createElementVNode("table", _hoisted_4, [
              _cache[7] || (_cache[7] = _createElementVNode("colgroup", null, [
                _createElementVNode("col", { class: "w-full" }),
                _createElementVNode("col")
              ], -1)),
              _cache[8] || (_cache[8] = _createElementVNode("thead", { class: "border-b border-gray-200 text-gray-900" }, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", {
                    scope: "col",
                    class: "px-0 py-3 font-semibold"
                  }, " Services "),
                  _createElementVNode("th", {
                    scope: "col",
                    class: "py-3 pr-0 pl-8 text-right font-semibold"
                  }, " Price ")
                ])
              ], -1)),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(servicesWithoutProcessingFees.value, (service, index) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: index + '-service',
                    class: "border-b border-gray-100"
                  }, [
                    _createElementVNode("td", _hoisted_5, [
                      _createElementVNode("div", _hoisted_6, _toDisplayString(service.description), 1)
                    ]),
                    _createElementVNode("td", _hoisted_7, _toDisplayString(_unref(currencyFormatter).format(service.total)), 1)
                  ]))
                }), 128))
              ]),
              _createElementVNode("tfoot", null, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", {
                    scope: "row",
                    class: _normalizeClass(["pt-4 font-semibold text-black sm:hidden", {'pb-4': showProcessingFee.value || !props.invoice.amountPaid}])
                  }, " Total ", 2),
                  _createElementVNode("th", {
                    scope: "row",
                    colspan: "1",
                    class: _normalizeClass(["hidden pt-4 text-right font-semibold text-black sm:table-cell", {'pb-4': showProcessingFee.value || !props.invoice.amountPaid}])
                  }, " Total ", 2),
                  _createElementVNode("td", {
                    class: _normalizeClass(["pt-4 pr-0 pl-8 text-right font-semibold text-black tabular-nums", [showProcessingFee.value || !props.invoice.amountPaid ? 'pb-4' : 'pb-0']])
                  }, _toDisplayString(_unref(currencyFormatter).format(+totalServicesWithoutProcessingFees.value)), 3)
                ]),
                (showProcessingFee.value)
                  ? (_openBlock(), _createElementBlock("tr", _hoisted_8, [
                      _createElementVNode("th", {
                        scope: "row",
                        class: _normalizeClass(["font-semibold text-black sm:hidden", {'pb-4': !props.invoice.amountPaid || props.invoice?.tax > 0}])
                      }, " Processing Fee ", 2),
                      _createElementVNode("th", {
                        scope: "row",
                        colspan: "1",
                        class: _normalizeClass(["hidden text-right font-semibold text-black sm:table-cell", {'pb-4': !props.invoice.amountPaid || props.invoice?.tax > 0}])
                      }, " Processing Fee ", 2),
                      _createElementVNode("td", {
                        class: _normalizeClass(["pr-0 pb-0 pl-8 text-right font-semibold text-black tabular-nums", {'pb-4': !props.invoice.amountPaid || props.invoice?.tax > 0}])
                      }, _toDisplayString(_unref(currencyFormatter).format(+__props.invoice.ccFee)), 3)
                    ]))
                  : _createCommentVNode("", true),
                (props.invoice?.tax > 0)
                  ? (_openBlock(), _createElementBlock("tr", _hoisted_9, [
                      _createElementVNode("th", {
                        scope: "row",
                        class: _normalizeClass(["font-semibold text-black sm:hidden", {'pb-4': !props.invoice.amountPaid}])
                      }, " Tax ", 2),
                      _createElementVNode("th", {
                        scope: "row",
                        colspan: "1",
                        class: _normalizeClass(["hidden text-right font-semibold text-black sm:table-cell", {'pb-4': !props.invoice.amountPaid}])
                      }, " Tax ", 2),
                      _createElementVNode("td", {
                        class: _normalizeClass(["pr-0 pl-8 text-right font-semibold text-black tabular-nums", {'pb-4': !props.invoice.amountPaid}])
                      }, _toDisplayString(_unref(currencyFormatter).format(+props.invoice?.tax)), 3)
                    ]))
                  : _createCommentVNode("", true),
                (props.invoice.amountPaid)
                  ? (_openBlock(), _createElementBlock("tr", _hoisted_10, [
                      _cache[3] || (_cache[3] = _createElementVNode("th", {
                        scope: "row",
                        class: "pb-4 pt-4 font-semibold text-black sm:hidden"
                      }, " Paid ", -1)),
                      _cache[4] || (_cache[4] = _createElementVNode("th", {
                        scope: "row",
                        colspan: "1",
                        class: "hidden pb-4 pt-4 text-right font-semibold text-black sm:table-cell"
                      }, " Paid ", -1)),
                      _createElementVNode("td", _hoisted_11, " -" + _toDisplayString(_unref(currencyFormatter).format(props.invoice.amountPaid)), 1)
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("tr", _hoisted_12, [
                  _cache[5] || (_cache[5] = _createElementVNode("th", {
                    scope: "row",
                    class: "pt-4 text-lg font-semibold text-gray-900 sm:hidden"
                  }, " Amount Due ", -1)),
                  _cache[6] || (_cache[6] = _createElementVNode("th", {
                    scope: "row",
                    colspan: "1",
                    class: "hidden pt-4 text-right text-lg font-semibold text-gray-900 sm:table-cell"
                  }, " Amount Due ", -1)),
                  _createElementVNode("td", _hoisted_13, _toDisplayString(_unref(currencyFormatter).format(amountDueAfterProcessingFees.value)), 1)
                ])
              ])
            ])
          ], 512), [
            [_vShow, stripeElementsLoaded.value]
          ]),
          _withDirectives(_createElementVNode("p", _hoisted_14, [
            _cache[9] || (_cache[9] = _createTextVNode(" A receipt will be sent to  ")),
            _createElementVNode("strong", null, _toDisplayString(contactEmail.value), 1)
          ], 512), [
            [_vShow, stripeElementsLoaded.value]
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_15, [
            _createVNode(_component_tw_button, {
              class: "btn-base btn-indigo",
              onClick: submitPayment,
              loading: isSubmitting.value,
              disabled: isSubmitting.value || !stripeElementsLoaded.value
            }, {
              default: _withCtx(() => _cache[10] || (_cache[10] = [
                _createTextVNode(" Confirm Payment ")
              ])),
              _: 1,
              __: [10]
            }, 8, ["loading", "disabled"])
          ], 512), [
            [_vShow, stripeElementsLoaded.value]
          ])
        ], 512), [
          [_vShow, !needsMicrodepositVerification.value]
        ]),
        _withDirectives(_createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_16, [
            _createVNode(_component_v_heading, null, {
              default: _withCtx(() => _cache[11] || (_cache[11] = [
                _createTextVNode(" Verify Your Bank Account ")
              ])),
              _: 1,
              __: [11]
            }),
            _cache[12] || (_cache[12] = _createElementVNode("p", { class: "text-sm/6" }, " We sent a small deposit to your bank account, which can take 1 to 2 business days to appear on your statement. Enter the deposit amount to finish paying this invoice. ", -1))
          ]),
          _createElementVNode("div", _hoisted_17, [
            _createVNode(_component_tw_button, {
              class: "btn-base btn-white",
              onClick: switchPaymentMethod,
              loading: isSwitchingPaymentMethod.value,
              disabled: isSwitchingPaymentMethod.value
            }, {
              default: _withCtx(() => _cache[13] || (_cache[13] = [
                _createTextVNode(" Use a Different Payment Method ")
              ])),
              _: 1,
              __: [13]
            }, 8, ["loading", "disabled"]),
            _createVNode(_component_tw_button, {
              class: "btn-base btn-indigo",
              onClick: openVerificationWindow,
              disabled: isSwitchingPaymentMethod.value || !verificationHostedUrl.value
            }, {
              default: _withCtx(() => _cache[14] || (_cache[14] = [
                _createTextVNode(" Verify Deposit ")
              ])),
              _: 1,
              __: [14]
            }, 8, ["disabled"])
          ])
        ], 512), [
          [_vShow, needsMicrodepositVerification.value]
        ])
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(_component_v_alert, {
      open: alertIsOpen.value,
      "on-close": closeAlert,
      static: "",
      size: "lg"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_alert_title, null, {
          default: _withCtx(() => _cache[15] || (_cache[15] = [
            _createTextVNode(" Payment Submitted ")
          ])),
          _: 1,
          __: [15]
        }),
        _createVNode(_component_v_alert_body, null, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_18, [
              _createVNode(_component_v_text, { class: "!text-base" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_strong, null, {
                    default: _withCtx(() => [
                      _cache[16] || (_cache[16] = _createTextVNode(" A receipt will be sent to ")),
                      _createElementVNode("span", _hoisted_19, _toDisplayString(contactEmail.value), 1),
                      _cache[17] || (_cache[17] = _createTextVNode(" when your payment has been processed. "))
                    ]),
                    _: 1,
                    __: [16,17]
                  })
                ]),
                _: 1
              }),
              (selectedPaymentMethod.value === 'us_bank_account')
                ? (_openBlock(), _createBlock(_component_v_text, {
                    key: 0,
                    class: "!text-base text-center"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_strong, { class: "!font-bold" }, {
                        default: _withCtx(() => _cache[18] || (_cache[18] = [
                          _createTextVNode(" This will take up to 4 business days for ACH payments. ")
                        ])),
                        _: 1,
                        __: [18]
                      })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }),
        _createVNode(_component_v_alert_actions, null, {
          default: _withCtx(() => [
            _createVNode(_component_tw_button, {
              class: "btn-base btn-indigo",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (closeAlert()))
            }, {
              default: _withCtx(() => _cache[19] || (_cache[19] = [
                _createTextVNode(" Okay ")
              ])),
              _: 1,
              __: [19]
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"])
  ]))
}
}

})